import { Fragment } from "react";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FieldMessage, fieldDescribedBy, helpTextId } from "./FieldValue";
import { RadioFieldProps } from "./RadioField.types";

/** Radio/enum editable field - a group of radio inputs, one per option. */
export function RadioField({
  label,
  name,
  value,
  options,
  onChange,
  disabled,
  inline = false,
  cards = false,
  icons = false,
  helpText,
  error,
  className = "",
}: RadioFieldProps) {
  return (
    <fieldset
      className={classNames("field-value", className)}
      aria-describedby={fieldDescribedBy(name, helpText, error)}
      aria-invalid={error ? true : undefined}
    >
      <legend className="field-value__label form-label">
        {label}
      </legend>
      <div
        className={
          icons
            ? "d-flex flex-wrap gap-1"
            : cards
            ? classNames(
                "d-flex gap-2",
                inline ? "flex-column flex-md-row" : "flex-column"
              )
            : inline
            ? "d-flex flex-wrap gap-3"
            : "d-flex flex-column gap-2"
        }
      >
        {options.map((option) => {
          const optionId = `${name}-${option.value}`;

          if (icons) {
            return (
              <Fragment key={optionId}>
                <input
                  id={optionId}
                  name={name}
                  type="radio"
                  className="visually-hidden"
                  checked={String(value) === String(option.value)}
                  onChange={() => onChange(option.value)}
                  disabled={disabled}
                />
                <label
                  className={classNames(
                    "border rounded-2 d-flex align-items-center justify-content-center gap-2 h-32 px-2 mb-0 cursor-pointer",
                    String(value) === String(option.value)
                      ? "radio-icon--checked"
                      : "bg-white text-gray-600 hover-bg-light"
                  )}
                  htmlFor={optionId}
                  title={option.label}
                >
                  {option.icon && <FontAwesomeIcon icon={option.icon} className="small" />}
                  <span>{option.label}</span>
                </label>
              </Fragment>
            );
          }

          if (cards) {
            return (
              <div className="radio-card-item d-flex" key={optionId}>
                <input
                  id={optionId}
                  name={name}
                  type="radio"
                  className="btn-check"
                  checked={String(value) === String(option.value)}
                  onChange={() => onChange(option.value)}
                  disabled={disabled}
                />
                <label
                  className={classNames(
                    "btn btn-outline-primary radio-card text-start w-100 py-2 px-3",
                    { "border-danger": error }
                  )}
                  htmlFor={optionId}
                >
                  <span className="d-block fw-semibold">{option.label}</span>
                  {option.description && (
                    <span className="d-block fw-normal small">
                      {option.description}
                    </span>
                  )}
                </label>
              </div>
            );
          }

          return (
            <div className="form-check" key={optionId}>
              <input
                id={optionId}
                name={name}
                type="radio"
                className={classNames("form-check-input", { "is-invalid": error })}
                checked={value === option.value}
                onChange={() => onChange(option.value)}
                disabled={disabled}
              />
              <label className="form-check-label" htmlFor={optionId}>
                {option.label}
              </label>
            </div>
          );
        })}
      </div>
      {helpText && (
        <p
          id={helpTextId(name)}
          className="form-text small text-body-secondary mb-0"
        >
          {helpText}
        </p>
      )}
      <FieldMessage name={name} error={error} />
    </fieldset>
  );
}
